import { useEffect, useState, type ReactNode } from 'react'
import { Button } from '~/components/common/Button'
import Typography from '~/components/common/Typography'
import {
  ANYPOINT_BUILDER_MARKETPLACE_URL,
  getAnypointStudioP2BrowserUrl,
  getAnypointStudioP2RepositoryUrl
} from '~/constants/pluginDownloadUrls'
import { bridge } from '~/services/bridge'
import type { PluginShellKind } from '~/services/bridge/types'
import { CLIENT_API_VERSION, VersionService } from '~/services/plugin/versionService'

type VersionInfo = {
  /** `GET /api/version` → `version` (deployed API contract, e.g. staging) */
  server: string
  /** Bundled webapp `CLIENT_API_VERSION` */
  client: string
  /** Thin-shell semver from the IDE plugin, if reported */
  shell?: string
  /** Anypoint Studio (Eclipse) vs Anypoint Builder (VS Code) for download actions */
  pluginShell: PluginShellKind
}

type GateState = 'idle' | 'checking' | 'ok' | 'blocked'

/**
 * In IDE plugin mode, verifies bundled webapp API version matches server `/api/version`
 * and honors server ERROR messages (thin shell / legacy plugin). Blocks the full UI when incompatible.
 */
export function PluginVersionGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>(() => (bridge.isInPlugin() ? 'checking' : 'idle'))
  const [messages, setMessages] = useState<string[]>([])
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null)

  useEffect(() => {
    if (!bridge.isInPlugin()) {
      setState('ok')
      return
    }

    let cancelled = false

    const run = async () => {
      setState('checking')
      const shellVersion = await bridge.getShellVersion()
      const pluginShell = bridge.getPluginShellKind()
      const service = new VersionService()
      const result = await service.checkVersion({ shellVersion })

      if (cancelled) return

      const info: VersionInfo = {
        server: result.serverVersion,
        client: CLIENT_API_VERSION,
        shell: shellVersion ?? undefined,
        pluginShell
      }
      setVersionInfo(info)

      if (result.isCompatible) {
        setState('ok')
        return
      }

      setMessages(result.messages.map(m => m.message).filter(Boolean))
      setState('blocked')
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [])

  if (!bridge.isInPlugin() || state === 'idle') {
    return <>{children}</>
  }

  if (state === 'ok' && versionInfo) {
    return (
      <div className='flex flex-col flex-1 min-h-0 min-w-0 w-full h-full'>
        {children}
        <div
          className='shrink-0 border-t border-tokens-neutral-border-1 px-3 py-2 bg-tokens-neutral-bg-secondary'
          role='status'
          aria-label='Deployed server and client versions'
          data-testid='plugin-version-footer'
        >
          <Typography
            fontFamily='geist'
            fontSize='labelSm'
            className='text-tokens-neutral-fg-secondary text-center'
          >
            Server API{' '}
            <span
              className='font-mono text-tokens-neutral-fg-primary'
              data-testid='plugin-version-server'
            >
              {versionInfo.server}
            </span>
            {' · '}
            Bundled UI{' '}
            <span
              className='font-mono text-tokens-neutral-fg-primary'
              data-testid='plugin-version-client'
            >
              {versionInfo.client}
            </span>
            {versionInfo.shell ? (
              <>
                {' · '}
                IDE shell{' '}
                <span className='font-mono text-tokens-neutral-fg-primary'>
                  {versionInfo.shell}
                </span>
              </>
            ) : null}
          </Typography>
        </div>
      </div>
    )
  }

  /* c8 ignore start -- defensive: ok in plugin before versionInfo is set (not reachable after check completes) */
  if (state === 'ok' && !versionInfo) {
    return <>{children}</>
  }
  /* c8 ignore stop */

  if (state === 'checking') {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen w-full p-8 bg-tokens-neutral-bg-primary'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4' />
        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          className='text-tokens-neutral-fg-secondary'
        >
          Checking compatibility…
        </Typography>
      </div>
    )
  }

  const studioP2RepoUrl = getAnypointStudioP2RepositoryUrl(bridge.getBaseUrl())
  const studioP2BrowserUrl = getAnypointStudioP2BrowserUrl(bridge.getBaseUrl())

  return (
    <div className='flex flex-col items-center justify-center min-h-screen w-full p-8 gap-6 bg-tokens-neutral-bg-primary'>
      <div className='flex flex-col items-center gap-4 max-w-lg text-center'>
        <div className='w-16 h-16 rounded-full bg-tokens-status-error-10 flex items-center justify-center'>
          <svg
            width='32'
            height='32'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='text-tokens-status-error-1'
            aria-hidden
          >
            <path
              d='M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
            />
          </svg>
        </div>
        <Typography
          fontFamily='geist'
          fontSize='headlineSm'
          fontWeight='semibold'
          className='text-tokens-neutral-fg-primary'
        >
          Update required
        </Typography>
        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          className='text-tokens-neutral-fg-secondary'
        >
          This version of the Curie IDE plugin is not compatible with the server, or the bundled UI
          is out of date. Install the latest plugin, then restart your IDE.
        </Typography>
        <div className='flex flex-col sm:flex-row gap-3 w-full justify-center mt-2'>
          {versionInfo?.pluginShell === 'studio' ? (
            <Button
              type='button'
              variant='primary'
              className='w-full sm:w-auto'
              data-testid='plugin-version-download-studio'
              onClick={() => void bridge.openInBrowser(studioP2BrowserUrl)}
            >
              Download Curie for Anypoint Studio
            </Button>
          ) : null}
          {versionInfo?.pluginShell === 'builder' ? (
            <Button
              type='button'
              variant='primary'
              className='w-full sm:w-auto'
              data-testid='plugin-version-download-builder'
              onClick={() => void bridge.openInBrowser(ANYPOINT_BUILDER_MARKETPLACE_URL)}
            >
              Download Curie for Anypoint Builder (VS Code)
            </Button>
          ) : null}
          {versionInfo?.pluginShell === 'unknown' ? (
            <>
              <Button
                type='button'
                variant='primary'
                className='w-full sm:w-auto'
                data-testid='plugin-version-download-studio'
                onClick={() => void bridge.openInBrowser(studioP2BrowserUrl)}
              >
                Download Curie for Anypoint Studio
              </Button>
              <Button
                type='button'
                variant='outlined'
                className='w-full sm:w-auto'
                data-testid='plugin-version-download-builder'
                onClick={() => void bridge.openInBrowser(ANYPOINT_BUILDER_MARKETPLACE_URL)}
              >
                Download Curie for Anypoint Builder (VS Code)
              </Button>
            </>
          ) : null}
        </div>
        {versionInfo?.pluginShell === 'unknown' ? (
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='text-tokens-neutral-fg-secondary'
          >
            Choose the download for the IDE you use (Anypoint Studio or Anypoint Builder).
          </Typography>
        ) : null}
        {versionInfo && (
          <div
            className='w-full mt-2 p-3 rounded-lg bg-tokens-neutral-fill-fill-6 border border-tokens-neutral-border-1 text-left'
            data-testid='plugin-version-blocked-details'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='medium'
              className='text-tokens-neutral-fg-primary'
            >
              Version details
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              className='text-tokens-neutral-fg-secondary mt-1'
            >
              Deployed server API:{' '}
              <span className='font-mono text-tokens-neutral-fg-primary'>{versionInfo.server}</span>
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              className='text-tokens-neutral-fg-secondary'
            >
              Bundled UI:{' '}
              <span className='font-mono text-tokens-neutral-fg-primary'>{versionInfo.client}</span>
            </Typography>
            {versionInfo.shell ? (
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-tokens-neutral-fg-secondary'
              >
                IDE shell:{' '}
                <span className='font-mono text-tokens-neutral-fg-primary'>
                  {versionInfo.shell}
                </span>
              </Typography>
            ) : null}
            {versionInfo.pluginShell === 'studio' || versionInfo.pluginShell === 'unknown' ? (
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-tokens-neutral-fg-secondary mt-1'
              >
                Studio update site:{' '}
                <span className='font-mono text-tokens-neutral-fg-primary'>{studioP2RepoUrl}</span>
                {' · '}
                Browser opens install instructions (Eclipse uses this repository URL).
              </Typography>
            ) : null}
          </div>
        )}
        {messages.length > 0 && (
          <ul className='text-left w-full space-y-2 mt-2'>
            {messages.map(msg => (
              <li key={msg}>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-tokens-neutral-fg-primary'
                >
                  {msg}
                </Typography>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
